import { Injectable } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  DynamicControlType,
  DynamicFieldDescriptor,
  DynamicSelectOption,
  DynamicSettingsForm,
  DynamicSettingsFormGroup,
  JsonSchemaProperty,
  SettingsJsonSchema,
} from './dynamic-extension.model';

@Injectable({ providedIn: 'root' })
export class DynamicExtensionFormService {
  buildForm(schema: SettingsJsonSchema | null | undefined, values: Record<string, unknown> = {}): DynamicSettingsForm {
    const formGroup: DynamicSettingsFormGroup = new FormGroup<Record<string, FormControl>>({});
    const fields: DynamicFieldDescriptor[] = [];
    const properties = schema?.properties ?? {};
    const required = schema?.required ?? [];

    for (const [key, property] of Object.entries(properties)) {
      const isRequired = required.includes(key);
      const controlType = this.resolveControlType(property);
      const descriptor: DynamicFieldDescriptor = {
        key,
        controlType,
        label: property.title ?? key,
        description: property.description,
        required: isRequired,
        options: controlType === DynamicControlType.SELECT ? this.buildOptions(property) : undefined,
      };

      if (controlType === DynamicControlType.UNSUPPORTED) {
        descriptor.rawValue = values?.[key] ?? property.default;
        fields.push(descriptor);
        continue;
      }

      const initialValue = this.resolveInitialValue(property, controlType, values?.[key]);
      formGroup.addControl(key, new FormControl(initialValue, this.buildValidators(property, controlType, isRequired)));
      fields.push(descriptor);
    }

    return { formGroup, fields };
  }

  private resolveControlType(property: JsonSchemaProperty): DynamicControlType {
    if (Array.isArray(property.enum) && property.enum.length > 0) {
      return DynamicControlType.SELECT;
    }
    switch (property.type) {
      case 'string':
        return DynamicControlType.TEXT;
      case 'number':
      case 'integer':
        return DynamicControlType.NUMBER;
      case 'boolean':
        return DynamicControlType.TOGGLE;
      default:
        return DynamicControlType.UNSUPPORTED;
    }
  }

  private buildOptions(property: JsonSchemaProperty): DynamicSelectOption[] {
    return (property.enum ?? []).map((value, index) => ({
      label: property.enumNames?.[index] ?? String(value),
      value,
    }));
  }

  private resolveInitialValue(
    property: JsonSchemaProperty,
    controlType: DynamicControlType,
    currentValue: unknown,
  ): unknown {
    if (currentValue !== undefined && currentValue !== null) {
      return currentValue;
    }
    if (property.default !== undefined && property.default !== null) {
      return property.default;
    }
    switch (controlType) {
      case DynamicControlType.TOGGLE:
        return false;
      case DynamicControlType.TEXT:
        return '';
      default:
        return null;
    }
  }

  private buildValidators(
    property: JsonSchemaProperty,
    controlType: DynamicControlType,
    isRequired: boolean,
  ): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (isRequired) {
      validators.push(Validators.required);
    }
    if (controlType === DynamicControlType.TEXT) {
      if (typeof property.minLength === 'number') validators.push(Validators.minLength(property.minLength));
      if (typeof property.maxLength === 'number') validators.push(Validators.maxLength(property.maxLength));
      if (property.pattern) validators.push(Validators.pattern(property.pattern));
    }
    if (controlType === DynamicControlType.NUMBER) {
      if (typeof property.minimum === 'number') validators.push(Validators.min(property.minimum));
      if (typeof property.maximum === 'number') validators.push(Validators.max(property.maximum));
    }
    return validators;
  }
}
